import React, { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  UserCheck,
  CalendarDays,
  Package,
  FileText,
  MessagesSquare,
  UserCog,
  FolderOpen,
  NotebookPen,
  Settings,
  LogOut,
  Menu,
  X,
  ExternalLink,
  BookOpen,
  BookMarked,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { fetchUnreviewedCount, MANUAL_URL } from "../../lib/api";
import Avatar from "../common/Avatar";

const links = [
  { to: "/crm", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/crm/leads", label: "Leads", icon: Users, mod: "leads" },
  { to: "/crm/clients", label: "Clients", icon: UserCheck, mod: "clients" },
  { to: "/crm/notes", label: "Notes", icon: NotebookPen, mod: "notes" },
  { to: "/crm/invoices", label: "Invoices", icon: FileText, mod: "invoices" },
  { to: "/crm/products", label: "Services", icon: Package, mod: "products" },
  { to: "/crm/books", label: "Books", icon: BookMarked, adminOnly: true },
  { to: "/crm/messages", label: "Messages", icon: MessagesSquare, mod: "messages" },
  { to: "/crm/appointments", label: "Appointments", icon: CalendarDays, mod: "appointments" },
  { to: "/crm/users", label: "Users", icon: UserCog, adminOnly: true },
  { to: "/crm/documents", label: "User Documents", icon: FolderOpen, badgeKey: "documents", mod: "documents" },
  { to: "/crm/settings", label: "Settings", icon: Settings, adminOnly: true },
];

const canAccess = (l, user) => {
  if (user?.role === "admin") return true;
  if (l.adminOnly) return false;
  if (!l.mod) return true; // Dashboard
  return (user?.modules || []).includes(l.mod);
};

export default function CrmLayout() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [docCount, setDocCount] = useState(0);

  useEffect(() => {
    if (!token) return;
    const load = () => fetchUnreviewedCount(token).then((r) => setDocCount(r.count || 0)).catch(() => {});
    load();
    const id = setInterval(load, 20000);
    return () => clearInterval(id);
  }, [token]);

  const handleLogout = () => {
    logout();
    navigate("/crm/login");
  };

  const SidebarContent = () => (
    <>
      <div className="px-6 py-4 border-b border-navy-light" />
      <nav className="flex-1 px-3 py-4 space-y-1">
        {links.filter((l) => canAccess(l, user)).map((l) => (
          <NavLink
            key={l.to}
            to={l.to}
            end={l.end}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? "bg-gold text-navy"
                  : "text-gray-300 hover:bg-navy-light hover:text-white"
              }`
            }
          >
            <l.icon className="h-5 w-5" />
            <span className="flex-1">{l.label}</span>
            {l.badgeKey === "documents" && docCount > 0 && (
              <span className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-xs font-semibold" data-testid="documents-badge">
                {docCount}
              </span>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="px-3 py-4 border-t border-navy-light space-y-1">
        <a
          href={MANUAL_URL}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-300 hover:bg-navy-light hover:text-white transition-colors"
          data-testid="download-manual-link"
        >
          <BookOpen className="h-5 w-5" /> User Manual
        </a>
        <a
          href="/"
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-300 hover:bg-navy-light hover:text-white transition-colors"
        >
          <ExternalLink className="h-5 w-5" /> View Website
        </a>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-300 hover:bg-red-500/20 hover:text-red-300 transition-colors"
        >
          <LogOut className="h-5 w-5" /> Sign Out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-cream flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 bg-navy flex-col fixed inset-y-0 left-0">
        <SidebarContent />
      </aside>

      {/* Mobile sidebar */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="relative w-64 bg-navy flex flex-col">
            <button onClick={() => setOpen(false)} className="absolute top-4 right-4 text-white">
              <X className="h-5 w-5" />
            </button>
            <SidebarContent />
          </aside>
        </div>
      )}

      <div className="flex-1 min-w-0 lg:ml-64">
        <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
          <div className="flex items-center justify-between px-4 sm:px-6 py-3.5">
            <button onClick={() => setOpen(true)} className="lg:hidden text-navy">
              <Menu className="h-6 w-6" />
            </button>
            <div className="hidden lg:block font-serif text-lg font-semibold text-navy">
              Welcome back, {user?.name?.split(" ")[0]}
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right hidden sm:block">
                <div className="text-sm font-medium text-navy">{user?.name}</div>
                <div className="text-xs text-gray-500">{user?.role}</div>
              </div>
              <div className="h-9 w-9 rounded-full bg-navy text-gold font-semibold flex items-center justify-center text-sm overflow-hidden">
                <Avatar
                  name={user?.name}
                  src={user?.avatar}
                  className="h-9 w-9 rounded-full bg-navy text-gold text-sm font-semibold"
                />
              </div>
            </div>
          </div>
        </header>
        <main className="p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
